/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  FormControl,
  IconButton,
  FormHelperText as MuiFormHelperText,
  InputAdornment,
  Typography,
  styled,
  Skeleton,
  Card,
} from '@mui/material';
import {useFormik} from 'formik';
import Button from '@jobseeker/lotus/elements/Button';
import {useIsLargeDevice, useValidateComponent} from '@jobseeker/lotus/hooks';
import {useAlertCallback} from '@jobseeker/lotus/hooks/alert';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useState} from 'react';
import Input from '@jobseeker/lotus/elements/Input';
import {Image} from '@jobseeker/lotus/components/Image';
import {UnlockHirist, LockHirist} from '@jobseeker/lotus/assets/icons';
import React from 'react';
import * as yup from 'yup';
import {Tooltip} from '@jobseeker/lotus/elements';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import UserService from '@jobseeker/common-core/services/user.service';
import {enqueueSnackbar} from 'notistack';
import Loader from '@jobseeker/lotus/components/Loader';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {forgotPassword} from '@jobseeker/feature-authentication/helpers';
import {useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';

const validationSchemaChangePassword = yup.object({
  password: yup
    .string()
    .required('Please enter your password')
    .matches(/^\S{6,32}$/, 'Password must be 6-32 characters with no spaces'),
  oldpassword: yup
    .string()
    .required('Please enter your password')
    .matches(/^\S{6,32}$/, 'Password must be 6-32 characters with no spaces'),
  // confirmpassword: yup
  //   .string()
  //   .required('Please enter your password')
  //   .oneOf(
  //     [yup.ref('password')],
  //     'New password and confirmation password do not match'
  //   ),
});

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
}));

const ChangePasswordSetting = (props: {loading: boolean}) => {
  const {loading} = props;
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const isLarge = useIsLargeDevice();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [handlePasswordRegexValidation, PasswordValidationComponent] =
    useValidateComponent(true);

  const profile = useSelector((state: AppState) => state.user.profile);
  const [ForgotAlert, toggleForgotAlert] = useAlertCallback({
    title: (
      <>
        We’ve sent an email to {profile.email || 'your email'}
        <br /> Click the link in the email to rest your password.
      </>
    ),
    subtitle:
      'If you don’t see the email, check other places it might be, like your junk, spam, social, or other folders.',
    primaryAction: 'Okay',
    primaryCallback: () => {},
  });

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const formik = useFormik({
    initialValues: {
      password: '',
      oldpassword: '',
    },
    validationSchema: validationSchemaChangePassword,
    onSubmit: async (values: any) => {
      // Trim all fields before sending to backend
      const trimmedValues = {
        password: values.password,
        oldpassword: values.oldpassword,
        confirmpassword: values.password,
      };
      setButtonLoading(true);
      const resp = await UserService.changePassword(trimmedValues);
      if (resp?.error) {
        formik.setFieldError(
          resp.error.name === 'USER_PASSWORD_MISMATCHED'
            ? 'oldpassword'
            : 'password',
          resp.error.message
        );
      } else {
        enqueueSnackbar(
          'Your password has been updated successfully. For your security, you have been logged out from all devices. Please log in again using your new password.',
          {variant: 'success'}
        );
      }
      setButtonLoading(false);
    },
    validateOnChange: true,
    validateOnBlur: true,
    validate: async values => {
      handlePasswordRegexValidation(values.password);
    },
  });

  return (
    <Box
      component={'form'}
      onSubmit={e => {
        e.preventDefault();
        formik.handleSubmit();
      }}
      sx={{height: '83%'}}
      px={{xxs: 1.25, md: 0}}
    >
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          marginLeft: {md: 0.625},
          px: {xxs: 1.875, md: 4.625},
          pb: {xxs: 1.875, md: 4.625},
          height: '100%',
        }}
      >
        <Box>
          {isLarge && (
            <Box display={'flex'}>
              <Typography
                display="flex"
                fontSize={{xxs: 16, md: 20}}
                fontWeight={400}
                m={0}
                mb={1}
                mt={2.75}
                sx={{
                  wordWrap: 'break-word',
                  textTransform: 'capitalize',
                }}
              >
                Change your password
              </Typography>
            </Box>
          )}
          <Box mt={isLarge ? 0 : 1.875}>
            {loading ? (
              <Box>
                <Box px={1} width={'100%'}>
                  <Skeleton
                    variant="rounded"
                    width={'80%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                    }}
                  />
                </Box>
                <Box px={1} width={'100%'}>
                  <Skeleton
                    variant="rounded"
                    width={'80%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                    }}
                  />
                </Box>
                <Box px={1} width={'100%'}>
                  <Skeleton
                    variant="rounded"
                    width={'80%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      mb: 1,
                    }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                    }}
                  />
                </Box>
                <Box px={1} width={'100%'}>
                  <Skeleton
                    variant="rounded"
                    width={'80%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      mb: 1,
                    }}
                  />
                  {[
                    ...new Array(5).fill('').map(() => (
                      <>
                        <Skeleton
                          variant="rounded"
                          width={'100%'}
                          height={10}
                          sx={{
                            my: 0.5,
                          }}
                        />
                      </>
                    )),
                  ]}
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                    }}
                  />
                </Box>
                <Box px={1} mb={4} width={'100%'}>
                  <Skeleton
                    variant="rounded"
                    width={'80%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                      mb: 1,
                    }}
                  />
                  {[
                    ...new Array(5).fill('').map(() => (
                      <>
                        <Skeleton
                          variant="rounded"
                          width={'100%'}
                          height={10}
                          sx={{
                            my: 0.5,
                          }}
                        />
                      </>
                    )),
                  ]}
                  <Skeleton
                    variant="rounded"
                    width={'50%'}
                    height={20}
                    sx={{
                      mt: {xxs: 0, md: 1},
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <>
                <Box>
                  <Box display="flex" width="100%" mt={{xxs: 0, md: 1.5}}>
                    <FormControl
                      sx={{
                        width: {xxs: '100%', md: '45%', lg: '46.7%'},
                        mr: {xxs: 0, md: 1.5},
                      }}
                      error={
                        formik.touched.oldpassword &&
                        Boolean(formik.errors.oldpassword)
                      }
                    >
                      <Typography
                        variant="subtitle2"
                        mb={1}
                        fontWeight={700}
                        sx={{color: '#212529'}}
                      >
                        Current Password
                      </Typography>
                      <Input
                        variant={VersionVariants.VERSION_ONE}
                        type={showPassword ? 'text' : 'password'}
                        name="oldpassword"
                        value={formik.values.oldpassword}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.oldpassword &&
                          Boolean(formik.errors.oldpassword)
                        }
                        placeholder="Enter current password"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                disableRipple
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  },
                                }}
                              >
                                {showPassword ? (
                                  <Image
                                    src={UnlockHirist}
                                    width={15}
                                    height={15}
                                    alt="show password"
                                  />
                                ) : (
                                  <Image
                                    src={LockHirist}
                                    width={15}
                                    height={15}
                                    alt="hide password"
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#ccc',
                            },
                            '&:hover fieldset': {
                              borderColor: '#ccc',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#ccc',
                            },
                          },
                        }}
                      />
                      <Box
                        display={'flex'}
                        justifyContent={'space-between'}
                        mb={1}
                      >
                        <FormHelperText
                          sx={{
                            opacity: Boolean(
                              formik.touched.oldpassword &&
                                formik.errors.oldpassword
                            )
                              ? 1
                              : 0,
                            width: '100%',
                            fontSize: '13px',
                            marginTop: '5px',
                          }}
                        >
                          <>{formik?.errors?.oldpassword || ''}</>
                        </FormHelperText>
                      </Box>
                      <Typography
                        component="a"
                        sx={{
                          textDecoration: 'none',
                          cursor: 'pointer',
                          display: 'inline-block',
                          fontSize: '16px',
                          marginTop: {xxs: '8px', md: '0px'},
                          fontWeight: 400,
                          color: '#0057ff',
                          textAlign: 'right',
                          fontFamily: 'Nunito Sans',
                        }}
                        onClick={async () => {
                          try {
                            const res = await forgotPassword(profile.email);
                            toggleForgotAlert();
                          } catch (err: any) {
                            enqueueSnackbar(
                              err?.message ||
                                'Failed to send reset email. Please try again.',
                              {variant: 'error'}
                            );
                          }
                        }}
                      >
                        Forgot Password?
                      </Typography>
                    </FormControl>
                  </Box>

                  {/* New Password and Confirm Password Fields */}
                  <Box
                    display="flex"
                    flexDirection={{xxs: 'column', md: 'row'}}
                    width="100%"
                    mt={2.125}
                    gap={{xxs: 4, md: 7.5}}
                  >
                    <FormControl
                      sx={{width: {xxs: '100%', md: '50%'}}}
                      error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                      }
                    >
                      <Typography
                        variant="subtitle2"
                        mb={1}
                        fontWeight={700}
                        sx={{color: '#212529'}}
                      >
                        New Password
                      </Typography>
                      <ClickAwayListener onClickAway={handleTooltipClose}>
                        <div>
                          <Tooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={handleTooltipClose}
                            open={open}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            title={<PasswordValidationComponent />}
                            arrow
                            placement="top-start"
                          >
                            <Box onClick={handleTooltipOpen}>
                              <Input
                                variant={VersionVariants.VERSION_ONE}
                                type={showNewPassword ? 'text' : 'Password'}
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                error={
                                  formik.touched.password &&
                                  Boolean(formik.errors.password)
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: '#ccc',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#ccc',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ccc',
                                    },
                                  },
                                }}
                                placeholder="Enter a new password"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        disableRipple
                                        aria-label="toggle password visibility"
                                        onClick={() =>
                                          setShowNewPassword(!showNewPassword)
                                        }
                                        sx={{
                                          '&:hover': {
                                            backgroundColor: 'transparent',
                                          },
                                        }}
                                      >
                                        {showNewPassword ? (
                                          <Image
                                            src={UnlockHirist}
                                            width={15}
                                            height={15}
                                            alt="show password"
                                          />
                                        ) : (
                                          <Image
                                            src={LockHirist}
                                            width={15}
                                            height={15}
                                            alt="hide password"
                                          />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </div>
                      </ClickAwayListener>
                      {formik.touched.password &&
                        Boolean(formik.errors.password) && (
                          <Box display={'flex'} mb={1}>
                            <FormHelperText
                              sx={{fontSize: '13px', marginTop: '5px'}}
                            >
                              <>{formik?.errors?.password || ''}</>
                            </FormHelperText>
                          </Box>
                        )}
                      <Typography
                        display="flex"
                        mt={1}
                        fontSize={14}
                        sx={{
                          lineHeight: 1.1,
                          opacity: 0.5,
                          wordWrap: 'break-word',
                          marginTop:
                            formik.touched.password && formik.errors.password
                              ? 2
                              : 1,
                        }}
                      >
                        Use 6-32 characters with no spaces.
                      </Typography>
                    </FormControl>

                    {/* <FormControl
                      sx={{width: {xxs: '100%', md: '50%'}}}
                      error={
                        formik.touched.confirmpassword &&
                        Boolean(formik.errors.confirmpassword)
                      }
                    >
                      <Typography
                        variant="subtitle2"
                        mb={1}
                        fontWeight={700}
                        sx={{color: '#212529'}}
                      >
                        Confirm Password
                      </Typography>
                      <Input
                        variant={VersionVariants.VERSION_ONE}
                        type={showConfirmPassword ? 'text' : 'Password'}
                        name="confirmpassword"
                        value={formik.values.confirmpassword}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.confirmpassword &&
                          Boolean(formik.errors.confirmpassword)
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#ccc',
                            },
                            '&:hover fieldset': {
                              borderColor: '#ccc',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#ccc',
                            },
                          },
                        }}
                        placeholder="Confirm your new password"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                disableRipple
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  },
                                }}
                              >
                                {showConfirmPassword ? (
                                  <Image
                                    src={UnlockHirist}
                                    width={15}
                                    height={15}
                                    alt="show password"
                                  />
                                ) : (
                                  <Image
                                    src={LockHirist}
                                    width={15}
                                    height={15}
                                    alt="hide password"
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {formik?.touched?.confirmpassword &&
                        Boolean(formik?.errors?.confirmpassword) && (
                          <Box display={'flex'} mb={1}>
                            <FormHelperText
                              sx={{fontSize: '13px', marginTop: '5px'}}
                            >
                              <>{formik?.errors?.confirmpassword || ''}</>
                            </FormHelperText>
                          </Box>
                        )}
                    </FormControl> */}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Card>
      <Box display={'flex'} gap={1.5} mt={4} mb={2} ml={{xxs: 0, md: 1}}>
        <Button
          sx={{
            width: {xxs: '100%', md: '211px'},
            fontSize: '16px',
            fontWeight: 500,
            backgroundColor: '#e9630c',
            px: 5,
            py: 1.75,
            borderRadius: '3px',
            height: '50px',
            '&:hover': {
              backgroundColor: '#e79e00',
            },
          }}
          variant="contained"
          type="submit"
        >
          {buttonLoading ? (
            <Loader
              size={20}
              sx={{
                color: '#ffffff !important',
              }}
            />
          ) : (
            'Change Password'
          )}
        </Button>
      </Box>
      <ForgotAlert />
    </Box>
  );
};

export default ChangePasswordSetting;
